import React, { useCallback, useEffect, useId, useRef } from 'react';
import styles from './AccountDialog.module.css';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal shell for the account hub's dialogs.
 *
 * Carries the accessibility contract its callers depend on: a labelled
 * `role="dialog"`, Escape to dismiss, focus moved in on open and handed back
 * to the trigger on close, and Tab kept inside the panel.
 */
export default function AccountDialog({
  open,
  onClose,
  title,
  lead,
  children,
  footer,
  tone,
  initialFocusRef,
}) {
  const overlayRef = useRef(null);
  const panelRef = useRef(null);
  const restoreFocusRef = useRef(null);
  const titleId = `${useId()}-title`;

  // Callers pass inline handlers; reading through a ref keeps a changing
  // handler identity from re-running the effect below, which would re-capture
  // the focus-restore target as an element inside the already-open dialog.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const requestClose = useCallback(() => onCloseRef.current?.(), []);

  useEffect(() => {
    if (!open) return undefined;

    restoreFocusRef.current = document.activeElement;
    const panel = panelRef.current;
    const target = initialFocusRef?.current || panel?.querySelector(FOCUSABLE) || panel;
    const raf = requestAnimationFrame(() => target?.focus?.({ preventScroll: true }));

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        requestClose();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;

      const items = Array.from(panelRef.current.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);

    const overlay = overlayRef.current;
    const viewport = window.visualViewport;
    const syncViewport = () => {
      if (!overlay || !viewport) return;
      overlay.style.top = `${viewport.offsetTop}px`;
      overlay.style.height = `${viewport.height}px`;
      overlay.style.bottom = 'auto';
      const active = document.activeElement;
      if (active && overlay.contains(active) && typeof active.scrollIntoView === 'function') {
        active.scrollIntoView({ block: 'nearest' });
      }
    };
    if (viewport) {
      syncViewport();
      viewport.addEventListener('resize', syncViewport);
      viewport.addEventListener('scroll', syncViewport);
    }

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown, true);
      if (viewport) {
        viewport.removeEventListener('resize', syncViewport);
        viewport.removeEventListener('scroll', syncViewport);
      }
      if (overlay) {
        overlay.style.top = '';
        overlay.style.height = '';
        overlay.style.bottom = '';
      }
      const restore = restoreFocusRef.current;
      if (restore && typeof restore.focus === 'function' && document.contains(restore)) {
        restore.focus({ preventScroll: true });
      }
    };
  }, [open, initialFocusRef, requestClose]);

  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      className={styles.overlay}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        ref={panelRef}
        className={[styles.panel, tone === 'danger' ? styles.panelDanger : ''].filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className={styles.head}>
          <h2 className={styles.title} id={titleId}>
            {title}
          </h2>
          <button type="button" className={styles.close} onClick={requestClose} aria-label="Close dialog">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12" />
              <path d="M18 6 6 18" />
            </svg>
          </button>
        </div>
        {lead ? <p className={styles.lead}>{lead}</p> : null}
        {children ? <div className={styles.body}>{children}</div> : null}
        {footer ? <div className={styles.footer}>{footer}</div> : null}
      </div>
    </div>
  );
}
